import { Box, Grid, Heading, Text, Button, Icon, useDisclosure, Flex, Link, GridItem, Tooltip } from '@chakra-ui/react';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillCreditCard, AiOutlineMail } from 'react-icons/ai';
// import { MdOutlineMail } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';

import { IconLocationPin } from 'assets/icons';
import Drawer from 'components/Drawer';
import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import { PLANS } from 'global/plans';
import { routePaths } from 'global/routePaths';
import { useCancelAddOns } from 'hooks/accountBilling/useCancelAddOns';
import { useFetchBillingDetails } from 'hooks/accountBilling/useFetchBillingDetails';
import { usePurchaseAddOns } from 'hooks/accountBilling/usePurchaseAddOns';
import { useResumePlan } from 'hooks/accountBilling/useResumePlan';
import { useCompanyInfo } from 'store/useUserStore';

import AccountCancellationModal from '../Modal/AccountCancellationModal';
import { AddPaymentMethod, ChangeAddress, ChangeBillingEmail } from '../RightDrawer';
import { useTableColumns } from './useTableColumns';
import { addOnDetails } from '../shared/billingData';

const initialTransactionTableQuery = {
  current_page: 1,
  first_page_url: '',
  from: 1,
  last_page: 1,
  last_page_url: '',
  next_page_url: '',
  path: '',
  per_page: '10',
  to: 1,
  total: 1,
};

const Subscription: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'accountBilling']);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isCancelModalOpen, onOpen: onCancelModalOpen, onClose: onCancelModalClose } = useDisclosure();
  const { isOpen: isPurchaseModalOpen, onOpen: onPurchaseModalOpen, onClose: onPurchaseModalClose } = useDisclosure();

  const [isYearly, setIsYearly] = useState<boolean>(true);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [addOnsId, setAddOnsId] = useState<string[]>([]);

  const { data, status: billingDetailsStatus } = useFetchBillingDetails();

  const { mutate: mutateOne } = useResumePlan();
  const { mutateAsync: purchaseAddOnFn, status: purchaseAddOnStatus } = usePurchaseAddOns();
  const { mutateAsync: cancelAddOnFn, status: cancelAddOnStatus } = useCancelAddOns();

  const companyInfo: any = useCompanyInfo();

  const { columns } = useTableColumns();

  // custom pagination starts //

  const [paginationQuery, setPaginationQuery] =
    useState<typeof initialTransactionTableQuery>(initialTransactionTableQuery);

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatePaginationQuery = (previousSearchQuery: any) => ({
      ...previousSearchQuery,
      current_page: selectedPageIndex + 1,
    });
    setPaginationQuery(updatePaginationQuery);
  }, []);

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationQuery((previousSearchQuery: any) => ({ ...previousSearchQuery, per_page: pageSize }));
  };

  const filterOutNull = (items: any[]) => {
    return items.filter((value: any) => value !== null && value !== undefined);
  };

  useEffect(() => {
    if (billingDetailsStatus === 'success') {
      if (data?.payload['add-ons']) {
        setSelectedAddOns(data?.payload['add-ons']);
      }
      const planDuration: string = (data?.payload?.duration_type as string).toLowerCase() || 'annual';
      if (planDuration === 'monthly') {
        // Toggle the plans to monthly
        setIsYearly(false);
      }
      const transactionsArray = data?.payload?.transactions ? filterOutNull(data.payload.transactions) : [];
      const updatePaginationQuery = {
        ...paginationQuery,
        total: transactionsArray.length,
        last_page: Math.ceil(transactionsArray.length / +paginationQuery.per_page) || 1,
      };

      setFilteredTransactions(transactionsArray);
      setPaginationQuery(updatePaginationQuery);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingDetailsStatus, data]);

  const currentItems = filteredTransactions.slice(
    (paginationQuery.current_page - 1) * +paginationQuery.per_page,
    paginationQuery.current_page * +paginationQuery.per_page
  );

  // custom pagination ends //

  const handleResume = () => {
    mutateOne();
  };

  const handlePurchaseAddOns = () => {
    const postData = {
      add_ons: addOnsId,
    };

    purchaseAddOnFn(postData).then(() => {
      onPurchaseModalClose();
      setAddOnsId([]);
    });
  };

  const handleCancelAddOns = () => {
    const postData = {
      add_ons: addOnsId,
    };

    cancelAddOnFn(postData).then(() => {
      onCancelModalClose();
      setAddOnsId([]);
    });
  };

  const newDate = new Date(data?.payload?.new_billing_date).toLocaleDateString(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });

  const total = data?.payload?.rates?.actual_rate * data?.payload?.total_users;
  const planAddOns = addOnDetails(isYearly, t);

  return (
    <React.Fragment>
      <Flex
        gridGap={3}
        mb={8}
        flexDir={['column', 'column', null, 'row']}
        sx={{
          '& > *': {
            flex: '0 1 auto',
            minWidth: '0',
            w: '100%',
          },
        }}
      >
        {/* Plan Details table */}
        <Box w="100%" color="newGray.1000" border="1px solid" borderColor="gray.200" borderRadius="md" bg="newGray.100">
          <Flex
            justifyContent="space-between"
            fontWeight="600"
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
            borderTopLeftRadius="md"
            borderTopRightRadius="md"
            px={4}
            py={4}
          >
            <Text>{t('accountBilling:subscriptionPlanDetails')}</Text>
            <Flex gridGap={4}>
              <Link as={RouterLink} to={routePaths.accountBilling.upgrade} color="newOrange.1000">
                <Text>{t('accountBilling:changePlan')}</Text>
              </Link>
              {!companyInfo?.is_canceled && (
                <Text
                  onClick={onOpen}
                  sx={{
                    cursor: 'pointer',
                    color: 'newOrange.1000',
                    _hover: {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('accountBilling:cancelPlan')}
                </Text>
              )}
              {companyInfo?.can_resume_subscription && (
                <Text
                  sx={{
                    cursor: 'pointer',
                    color: 'newOrange.1000',
                    _hover: {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => handleResume()}
                >
                  {t('accountBilling:resumePlan')}
                </Text>
              )}
            </Flex>
          </Flex>
          <Box>
            <Grid
              templateColumns="1fr 1fr"
              sx={{
                '& > *': {
                  py: 4,
                  px: 6,
                },
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
                borderBottom: '1px dashed',
                borderColor: 'newGray.200',
              }}
            >
              <GridItem>
                <Text fontSize="xs" mb={2}>
                  {t('accountBilling:selectedPlan')}
                </Text>
                <Text fontWeight="600">{`${companyInfo?.plan} - ${data?.payload?.duration_type}`}</Text>
              </GridItem>
              <GridItem>
                <Text fontSize="xs" mb={2}>
                  {companyInfo?.is_canceled ? t('accountBilling:accountExpiresOn') : t('accountBilling:nextInvoiceOn')}
                </Text>
                <Text fontWeight="600">{newDate}</Text>
              </GridItem>
            </Grid>
            <Grid
              templateColumns="1fr 1fr "
              sx={{
                '& > *': {
                  py: 4,
                  px: 6,
                },
                textTransform: 'lowercase',
                '& > * > *:first-letter': {
                  textTransform: 'capitalize',
                },
                whiteSpace: 'nowrap',
                borderBottom: '1px dashed',
                borderColor: 'newGray.200',
              }}
            >
              <Box textTransform="lowercase">
                <Text fontSize="xs" mb={2}>
                  {t('accountBilling:costPerUser')}
                </Text>
                <Text fontWeight="600">${(data?.payload?.rates?.actual_rate as number).toFixed(2)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" mb={2}>
                  {t('accountBilling:totalPrice')}
                </Text>
                <Text fontWeight="600">${(total as number).toFixed(2)}</Text>
              </Box>
            </Grid>
            <Flex alignItems="center" justifyContent="space-between" px={6} py={4}>
              {!companyInfo?.is_canceled && (
                <Text color="newGray.1000" fontSize="md" fontWeight="600">
                  {t('accountBilling:upcomingPaymentOf')} ${(data?.payload?.total_invoice as number).toFixed(2)}{' '}
                  <Box as="span" textTransform="lowercase">
                    {t('common:on')}
                  </Box>{' '}
                  {newDate}
                </Text>
              )}
            </Flex>
          </Box>
        </Box>
        {/* Add-Ons table */}
        {companyInfo?.plan?.toLowerCase() === PLANS.pro || companyInfo?.plan?.toLowerCase() === PLANS.premium ? (
          <Box color="newGray.1000" border="1px solid" borderColor="gray.200" borderRadius="md" bg="newGray.100">
            <Flex
              justifyContent="space-between"
              fontWeight="600"
              borderBottom="1px solid"
              borderColor="gray.200"
              bg="white"
              borderTopLeftRadius="md"
              borderTopRightRadius="md"
              px={6}
              py={4}
            >
              <Text>{t('accountBilling:addOns')}</Text>
            </Flex>
            <Box
              sx={{
                '& > *': {
                  py: 2,
                  px: 3,
                },
              }}
              py={2}
              px={3}
            >
              {planAddOns?.map((addOn, index: number) => (
                <Flex
                  key={index}
                  p={2}
                  bg="white"
                  border="1px solid"
                  borderColor="newGray.200"
                  borderRadius="base"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Text fontWeight={600}>
                    <Text as="span" mr={3} ml={1}>
                      <Icon
                        as={addOn.headingIcon}
                        sx={{
                          path: { fill: 'primary.500' },
                        }}
                      />
                    </Text>
                    {addOn.heading}
                  </Text>

                  {selectedAddOns.includes(addOn.id) ? (
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      isLoading={cancelAddOnStatus === 'loading'}
                      onClick={() => {
                        onCancelModalOpen();
                        setAddOnsId(prev => [...prev, addOn.id]);
                      }}
                    >
                      {t('common:cancel')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      colorScheme="green"
                      variant="ghost"
                      isLoading={purchaseAddOnStatus === 'loading'}
                      onClick={() => {
                        onPurchaseModalOpen();
                        setAddOnsId(prev => [...prev, addOn.id]);
                      }}
                    >
                      {t('common:purchase')}
                    </Button>
                  )}
                </Flex>
              ))}
            </Box>
          </Box>
        ) : null}
        {/* Billing Information table */}
        <Box color="newGray.1000" border="1px solid" borderColor="gray.200" borderRadius="md" bg="newGray.100">
          <Flex
            justifyContent="space-between"
            fontWeight="600"
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
            borderTopLeftRadius="md"
            borderTopRightRadius="md"
            px={6}
            py={4}
          >
            <Text>{t('accountBilling:billingInfo')}</Text>
          </Flex>
          <Box
            sx={{
              '& > *': {
                py: 2,
                px: 6,
              },
            }}
          >
            <Box>
              <Flex>
                <Drawer
                  drawerTrigger={onOpen => (
                    <Text
                      onClick={onOpen}
                      sx={{
                        mb: 2,
                        fontSize: 'sm',
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: 'newOrange.1000',
                        _hover: {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {t('accountBilling:changePaymentMethod')}
                    </Text>
                  )}
                  header={<div></div>}
                >
                  {onClose => <AddPaymentMethod onClose={onClose} />}
                </Drawer>
              </Flex>
              <Flex
                justifyContent="space-between"
                px={4}
                py={2}
                bg="white"
                border="1px solid"
                borderColor="newGray.200"
                borderRadius="md"
              >
                <Flex gridGap={4}>
                  <Flex
                    border="1px solid"
                    borderColor="newGray.200"
                    px={1.5}
                    py={1}
                    borderRadius="base"
                    alignItems="center"
                  >
                    <Icon as={AiFillCreditCard} color="primary.700" />
                  </Flex>
                  <Text textTransform="capitalize">
                    {data?.payload?.payment_method?.brand} ending with {data?.payload?.payment_method?.last4}
                  </Text>
                </Flex>
              </Flex>
            </Box>
            <Flex gridGap={4} pb={2} flexDir="column" justifyContent="space-evenly">
              <Box w="100%">
                <Flex alignItems="center" justifyContent="space-between" mb={2}>
                  {/* <Text fontWeight={600}>{t('accountBilling:billingAddress')}</Text> */}

                  <Drawer
                    drawerTrigger={onOpen => (
                      <Text
                        onClick={onOpen}
                        sx={{
                          fontSize: 'sm',
                          fontWeight: 600,
                          cursor: 'pointer',
                          color: 'newOrange.1000',
                          _hover: {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('accountBilling:changAddress')}
                      </Text>
                    )}
                    header={<div></div>}
                  >
                    {onClose => <ChangeAddress onClose={onClose} />}
                  </Drawer>
                </Flex>
                <Flex
                  justifyContent="space-between"
                  px={4}
                  py={3}
                  bg="white"
                  border="1px solid"
                  borderColor="newGray.200"
                  borderRadius="md"
                >
                  {data?.payload?.billing_address ? (
                    <Tooltip
                      label={
                        <Text>
                          {data?.payload?.billing_address?.address_1}, {data?.payload?.billing_address?.address_2}
                          {data?.payload?.billing_address?.city}, {data?.payload?.billing_address?.state},{' '}
                          {data?.payload?.billing_address?.country} - {data?.payload?.billing_address?.zip_code}
                        </Text>
                      }
                    >
                      <Flex gridGap={3} cursor="pointer" w="100%">
                        <Flex
                          border="1px solid"
                          borderColor="newGray.200"
                          px={1.5}
                          py={1}
                          borderRadius="base"
                          alignItems="center"
                        >
                          <Icon
                            as={IconLocationPin}
                            sx={{
                              path: {
                                fill: 'primary.700',
                              },
                            }}
                          />
                        </Flex>
                        <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" w="100%">
                          {data?.payload?.billing_address?.address_1}, {data?.payload?.billing_address?.address_2}
                          {data?.payload?.billing_address?.city}, {data?.payload?.billing_address?.state},{' '}
                          {data?.payload?.billing_address?.country} - {data?.payload?.billing_address?.zip_code}
                        </Text>
                      </Flex>
                    </Tooltip>
                  ) : null}
                </Flex>
              </Box>
              <Flex flexDir="column" w="100%">
                <Flex alignItems="center" justifyContent="space-between" mb={2}>
                  <Drawer
                    drawerTrigger={onOpen => (
                      <Text
                        onClick={onOpen}
                        sx={{
                          fontSize: 'sm',
                          fontWeight: 600,
                          cursor: 'pointer',
                          color: 'newOrange.1000',
                          _hover: {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('accountBilling:changeEmailAddress')}
                      </Text>
                    )}
                    header={<div></div>}
                  >
                    {onClose => (
                      <ChangeBillingEmail onClose={onClose} currentBillingEmail={data?.payload?.billing_email} />
                    )}
                  </Drawer>
                </Flex>

                <Flex
                  gridGap={3}
                  alignItems="center"
                  px={4}
                  py={3}
                  bg="white"
                  border="1px solid"
                  borderColor="newGray.200"
                  borderRadius="md"
                  w="100%"
                  h="100%"
                >
                  <Flex
                    border="1px solid"
                    borderColor="newGray.200"
                    px={1.5}
                    py={1}
                    borderRadius="base"
                    alignItems="center"
                  >
                    <Icon as={AiOutlineMail} color="primary.700" />
                  </Flex>
                  <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" w="100%">
                    <a href={`mailto:${data?.payload?.billing_email}`}>{data?.payload?.billing_email}</a>
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Flex>

      {/* Transactions start here */}
      <Box
        color="newGray.1000"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        bg="newGray.100"
        sx={{
          color: 'newGray.1000',
          border: '1px solid',
          borderColor: 'gray.200',
          borderRadius: 'md',
          bg: 'newGray.100',
          '& > *': {
            px: 6,
            py: 4,
          },
        }}
      >
        <Box
          sx={{
            bg: 'white',
            borderTopLeftRadius: 'md',
            borderTopRightRadius: 'md',
            borderBottom: '1px solid',
            borderColor: 'gray.200',
          }}
        >
          <Heading as="h4" fontWeight="bold" fontSize="md">
            {t('common:transactions')}
          </Heading>
        </Box>
        <Table
          columns={columns}
          data={currentItems}
          enableRowSelect
          paginationData={paginationQuery}
          paginationStyle="rowsPerPage"
          onPageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          styles={{ bodyStyles: { bg: 'white' } }}
        />
      </Box>

      <ConfirmationModal
        title="Purchase Segmented Tracking"
        subTitle={`You will be billed an additional $${isYearly ? '55 a year' : '5 a month'} per user`}
        confirmButtonLabel="Proceed with Purchase"
        confirmButtonProps={{
          colorScheme: 'green',
          variant: 'ghost',
          isLoading: purchaseAddOnStatus === 'loading',
          onClick: handlePurchaseAddOns,
        }}
        cancelButtonProps={{
          colorScheme: 'red',
          variant: 'ghost',
        }}
        isOpen={isPurchaseModalOpen}
        onClose={() => {
          setAddOnsId([]);
          onPurchaseModalClose();
        }}
      />

      <ConfirmationModal
        title="Cancel Segmented Tracking"
        subTitle={'Your subscription to Segmented Tracking will be canceled on the next billing period.'}
        confirmButtonLabel="Proceed with Cancelation"
        confirmButtonProps={{
          colorScheme: 'red',
          variant: 'ghost',
          isLoading: cancelAddOnStatus === 'loading',
          onClick: handleCancelAddOns,
        }}
        cancelButtonLabel="Don't cancel"
        cancelButtonProps={{
          colorScheme: 'primary',
          variant: 'ghost',
        }}
        isOpen={isCancelModalOpen}
        onClose={() => {
          setAddOnsId([]);
          onCancelModalClose();
        }}
      />

      <AccountCancellationModal isOpen={isOpen} onClose={onClose} />
    </React.Fragment>
  );
};

export default Subscription;
